import { useState } from "react";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  CheckboxLabel,
  HelperText,
  Link,
  TextField,
  TextFieldInput,
  TextFieldLabel,
} from "@hireart/component-library";
import { useFormik } from "formik";
import googleIcon from "hireart-icons/google.svg";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { getMetaContent } from "../../Common/helpers/meta";
import hireartLogo from "../assets/images/hireart-logo-secondary-green-neutral.svg";
import { redirectTo, signIn } from "./Login/utils";
import { validationSchema } from "./Login/validation-schema";

const FLASH_VARIANT = {
  error: "error",
  alert: "error",
  warning: "warning",
  success: "success",
  notice: "info",
  info: "info",
};

export const LoginUnconnected = ({ session = undefined }) => {
  const [serverError, setServerError] = useState("");
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: { email: "", password: "", rememberMe: false },
    validationSchema,
    onSubmit: async (values) => {
      setServerError("");
      try {
        const data = await signIn(values);
        if (!data) return;
        const userReturnTo = session?.user_return_to;
        redirectTo(userReturnTo || data.user_root_path);
      } catch (e) {
        setServerError(e.message);
      }
    },
  });

  const flashEntries = session?.flash?.flashes
    ? Object.entries(session.flash.flashes).filter(([, msg]) => Boolean(msg))
    : [];

  const showPlaintextAuth =
    typeof document === "undefined" ||
    document.cookie.indexOf("hide_plaintext_auth") === -1;

  const { isSubmitting } = formik;
  // Clicking the Google button blurs the autofocused email field, which marks
  // it touched and surfaces a "required" error before the OAuth POST navigates
  // away. Suppress field errors while the Google sign-in is in flight.
  const emailHasError =
    !isGoogleSubmitting && formik.touched.email && Boolean(formik.errors.email);
  const passwordHasError =
    !isGoogleSubmitting &&
    formik.touched.password &&
    Boolean(formik.errors.password);

  const handleRememberMeChange = (e) =>
    formik.setFieldValue("rememberMe", e.target.checked);

  return (
    <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
      <img
        alt="HireArt"
        className="mx-auto mb-16 h-80 w-80"
        src={hireartLogo}
      />
      <h1 className="headline-1 mb-24 text-center">Welcome to HireArt</h1>

      {serverError && (
        <Banner className="mb-16" variant="error">
          {serverError}
        </Banner>
      )}

      {!serverError &&
        flashEntries.map(([type, message]) => (
          <Banner
            key={type}
            className="mb-16"
            variant={FLASH_VARIANT[type] ?? "info"}
          >
            {message}
          </Banner>
        ))}

      <form action="/users/auth/google_oauth2" method="post">
        <input
          type="hidden"
          name="authenticity_token"
          value={getMetaContent("csrf-token")}
        />
        <Button
          color="secondary"
          iconStart={googleIcon}
          isDisabled={isSubmitting}
          isFullWidth
          isLoading={isGoogleSubmitting}
          onClick={() => setIsGoogleSubmitting(true)}
          type="submit"
        >
          Sign in with Google
        </Button>
      </form>

      {showPlaintextAuth ? (
        <>
          <hr className="my-24 border-t border-solid border-neutral-20" />

          <form noValidate onSubmit={formik.handleSubmit}>
            <TextField hasError={emailHasError} isDisabled={isSubmitting}>
              <TextFieldLabel htmlFor="email" isOptional isUnstyled>
                Email
              </TextFieldLabel>
              <TextFieldInput
                aria-describedby={emailHasError ? "email-error" : undefined}
                autoComplete="email"
                autoFocus
                id="email"
                name="email"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                placeholder="Email"
                readOnly={isSubmitting}
                size="md"
                type="email"
                value={formik.values.email}
              />
            </TextField>
            {emailHasError && (
              <HelperText id="email-error" hasError>
                {formik.errors.email}
              </HelperText>
            )}

            <TextField
              className="mt-16"
              hasError={passwordHasError}
              isDisabled={isSubmitting}
            >
              <TextFieldLabel htmlFor="password" isOptional isUnstyled>
                Password
              </TextFieldLabel>
              <TextFieldInput
                aria-describedby={
                  passwordHasError ? "password-error" : undefined
                }
                autoComplete="current-password"
                id="password"
                name="password"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                placeholder="Password"
                readOnly={isSubmitting}
                size="md"
                type="password"
                value={formik.values.password}
              />
            </TextField>
            {passwordHasError && (
              <HelperText id="password-error" hasError>
                {formik.errors.password}
              </HelperText>
            )}

            <Button
              className="mt-16"
              isDisabled={isSubmitting}
              isFullWidth
              isLoading={isSubmitting}
              type="submit"
            >
              Sign in
            </Button>

            <div className="mt-16 flex items-center justify-between">
              <CheckboxLabel className="body-2 flex items-center">
                <Checkbox
                  isChecked={formik.values.rememberMe}
                  isDisabled={isSubmitting}
                  name="rememberMe"
                  onChange={handleRememberMeChange}
                />
                Remember me
              </CheckboxLabel>
              <Link size="md" to="/forgot_password">
                Forgot your password?
              </Link>
            </div>
          </form>
        </>
      ) : (
        <p
          className="body-3 mt-16 text-center italic text-neutral-70"
          id="hireart-admin-plaintext-auth-explainer"
        >
          HireArt Admins may only login via Google
        </p>
      )}
    </Card>
  );
};

LoginUnconnected.propTypes = {
  session: PropTypes.shape({
    user_return_to: PropTypes.string,
    flash: PropTypes.shape({
      flashes: PropTypes.objectOf(PropTypes.string),
    }),
  }),
};

const mapStateToProps = ({ session }) => ({ session });

export const Login = connect(mapStateToProps)(LoginUnconnected);
